import { useCallback, useEffect, useRef } from 'react';
import { type CopilotSurface } from 'shared/copilot/copilotSurfaces';
import { EMPTY_SOURCE_SELECTION, type PersistedSourceSelection, readLastSourceSelection, resolveSelectionForChat, writeChatSourceSelection } from 'shared/copilot/utils/copilotSourceSelectionStorage';
interface AppliedSlot {
  clientId: number;
  chatId: string | null;
}
interface UseCopilotSourceSelectionPersistenceArgs {
  clientId: number;
  surface: CopilotSurface;
  currentChatId: string | null;
  enabled: boolean;
  selection: PersistedSourceSelection;
  applySelection: (selection: PersistedSourceSelection) => void;
}

/**
 * Restores picker selection when the open chat changes, and writes the per-chat
 * slot on send. Last-used is written by the picker callbacks themselves.
 */
export const useCopilotSourceSelectionPersistence = ({
  clientId,
  surface,
  currentChatId,
  enabled,
  selection,
  applySelection
}: UseCopilotSourceSelectionPersistenceArgs): {
  restoreSourceSelection: (chatId: string | null) => PersistedSourceSelection;
  persistSourceSelectionOnSend: () => void;
  rememberUnsentDraft: (snapshot: PersistedSourceSelection) => void;
} => {
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const applySelectionRef = useRef(applySelection);
  applySelectionRef.current = applySelection;
  const appliedSlotRef = useRef<AppliedSlot | null>(null);
  const seenChatIdRef = useRef<string | null | undefined>(undefined);
  /** First send on a draft happens before Flow assigns a chat id. */
  const pendingDraftRef = useRef<PersistedSourceSelection | null>(null);
  const restoreSourceSelection = useCallback((chatId: string | null): PersistedSourceSelection => {
    const next = chatId != null ? resolveSelectionForChat(clientId, surface, chatId) : readLastSourceSelection(clientId, surface) ?? EMPTY_SOURCE_SELECTION;
    appliedSlotRef.current = {
      clientId,
      chatId
    };
    pendingDraftRef.current = null;
    applySelectionRef.current(next);
    return next;
  }, [clientId, surface]);
  useEffect(() => {
    if (!enabled) return;
    const previousChatId = seenChatIdRef.current;
    seenChatIdRef.current = currentChatId;
    const applied = appliedSlotRef.current;
    if (applied?.clientId === clientId && applied.chatId === currentChatId) return;

    // Draft send: write the held snapshot once Flow assigns an id. Do not restore —
    // that would replace the picker with an empty chat slot.
    if (previousChatId === null && currentChatId != null && pendingDraftRef.current != null) {
      writeChatSourceSelection(clientId, currentChatId, surface, pendingDraftRef.current);
      pendingDraftRef.current = null;
      appliedSlotRef.current = {
        clientId,
        chatId: currentChatId
      };
      return;
    }

    // loadChat/createNewChat already applied the target slot; the chat id prop can lag.
    if (applied?.clientId === clientId) return;
    restoreSourceSelection(currentChatId);
  }, [clientId, currentChatId, enabled, restoreSourceSelection, surface]);
  const persistSourceSelectionOnSend = useCallback(() => {
    if (!enabled) return;
    const snapshot = selectionRef.current;
    if (currentChatId != null) {
      writeChatSourceSelection(clientId, currentChatId, surface, snapshot);
      pendingDraftRef.current = null;
      return;
    }
    pendingDraftRef.current = snapshot;
  }, [clientId, currentChatId, enabled, surface]);
  const rememberUnsentDraft = useCallback((snapshot_0: PersistedSourceSelection) => {
    pendingDraftRef.current = snapshot_0;
  }, []);
  return {
    restoreSourceSelection,
    persistSourceSelectionOnSend,
    rememberUnsentDraft
  };
};